import type { CodeFileState, EditorError } from 'mini-code'
import { create } from 'zustand'
import { persist, type PersistStorage, subscribeWithSelector } from 'zustand/middleware'
import { api, type User } from './api.ts'
import { type NumberWithParamsInfo, type PatternEventInfo, type SequenceData, type AmplitudeInfo, type LevelInfo, type TimelineInfo, type WaveformInfo } from './bytecode.ts'
import { dspscript } from './dsp-script.ts'

interface PlaybackState {
  isPlaying: boolean
  playingTabId: string | null
  setIsPlaying: (playing: boolean) => void
  setPlayingTabId: (tabId: string | null) => void
}

export const usePlaybackStore = create<PlaybackState>(set => ({
  isPlaying: false,
  playingTabId: null,
  setIsPlaying: (playing: boolean) => set({ isPlaying: playing }),
  setPlayingTabId: (tabId: string | null) => set({ playingTabId: tabId }),
}))

export interface EditorTab {
  id: string
  title: string
  artist: string
  codeA: string
  codeB: string
  trackId?: string
  editingTrackId?: string
  isDirty: boolean
  codeFileStateA?: CodeFileState
  codeFileStateB?: CodeFileState
  numbersWithParamsA?: NumberWithParamsInfo[]
  numbersWithParamsB?: NumberWithParamsInfo[]
  patternEventsA?: PatternEventInfo[]
  patternEventsB?: PatternEventInfo[]
  sequencesA?: SequenceData[]
  sequencesB?: SequenceData[]
  amplitudeInfosA?: AmplitudeInfo[]
  amplitudeInfosB?: AmplitudeInfo[]
  waveformInfosA?: WaveformInfo[]
  waveformInfosB?: WaveformInfo[]
  levelInfosA?: LevelInfo[]
  levelInfosB?: LevelInfo[]
  timelineInfosA?: TimelineInfo[]
  timelineInfosB?: TimelineInfo[]
}

interface TabsState {
  tabs: EditorTab[]
  activeTabId: string
  setTabs: (tabs: EditorTab[] | ((prev: EditorTab[]) => EditorTab[])) => void
  setActiveTabId: (id: string) => void
  addTab: (tab: EditorTab) => void
  updateTab: (id: string, updates: Partial<EditorTab>) => void
  closeTab: (id: string) => void
  createNewTab: () => void
  reorderTab: (fromIndex: number, toIndex: number) => void
}

export const useTabsStore = create<TabsState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        tabs: [
          {
            id: 'default',
            title: 'Untitled',
            artist: '',
            codeA: '',
            codeB: '',
            isDirty: false,
          },
        ],
        activeTabId: 'default',
        setTabs: tabs =>
          set({
            tabs: typeof tabs === 'function' ? tabs(get().tabs) : tabs,
          }),
        setActiveTabId: id => set({ activeTabId: id }),
        addTab: tab =>
          set(state => ({
            tabs: [...state.tabs, tab],
            activeTabId: tab.id,
          })),
        updateTab: (id, updates) =>
          set(state => {
            const tab = state.tabs.find(t => t.id === id)
            if (!tab) return state

            // Check if any values actually changed (excluding codeFileState and widget arrays which are always new objects)
            const hasChanges = Object.keys(updates).some(key => {
              const typedKey = key as keyof EditorTab
              if (typedKey === 'codeFileStateA' || typedKey === 'codeFileStateB'
                || typedKey === 'numbersWithParamsA' || typedKey === 'numbersWithParamsB'
                || typedKey === 'patternEventsA' || typedKey === 'patternEventsB'
                || typedKey === 'sequencesA' || typedKey === 'sequencesB'
                || typedKey === 'amplitudeInfosA' || typedKey === 'amplitudeInfosB'
                || typedKey === 'waveformInfosA' || typedKey === 'waveformInfosB'
                || typedKey === 'levelInfosA' || typedKey === 'levelInfosB'
                || typedKey === 'timelineInfosA' || typedKey === 'timelineInfosB')
              {
                // Always update these as they're new objects/arrays each time
                return true
              }
              return tab[typedKey] !== updates[typedKey]
            })

            if (!hasChanges) return state

            return {
              tabs: state.tabs.map(t => (t.id === id ? { ...t, ...updates } : t)),
            }
          }),
        closeTab: id =>
          set(state => {
            if (state.tabs.length === 1) {
              // Don't close the last tab, just reset it
              return {
                tabs: [
                  {
                    id: 'default',
                    title: 'Untitled',
                    artist: '',
                    codeA: '',
                    codeB: '',
                    isDirty: false,
                  },
                ],
                activeTabId: 'default',
              }
            }

            const tabIndex = state.tabs.findIndex(t => t.id === id)
            const newTabs = state.tabs.filter(t => t.id !== id)

            let newActiveTabId = state.activeTabId
            if (state.activeTabId === id) {
              // Switch to adjacent tab
              const newActiveIndex = Math.max(0, tabIndex - 1)
              newActiveTabId = newTabs[newActiveIndex]!.id
            }

            return {
              tabs: newTabs,
              activeTabId: newActiveTabId,
            }
          }),
        createNewTab: () =>
          set(state => {
            const newTab: EditorTab = {
              id: `tab-${Date.now()}`,
              title: 'Untitled',
              artist: '',
              codeA: '',
              codeB: '',
              isDirty: false,
            }
            return {
              tabs: [...state.tabs, newTab],
              activeTabId: newTab.id,
            }
          }),
        reorderTab: (fromIndex, toIndex) =>
          set(state => {
            if (fromIndex === toIndex) return state
            const newTabs = [...state.tabs]
            const [movedTab] = newTabs.splice(fromIndex, 1)
            newTabs.splice(toIndex, 0, movedTab!)
            return { tabs: newTabs }
          }),
      }),
      {
        name: 'dspscript-tabs',
      },
    ),
  ),
)

interface ChannelModeState {
  channelMode: 'single' | 'dual'
  setChannelMode: (mode: 'single' | 'dual') => void
}

export const useChannelModeStore = create<ChannelModeState>()(
  persist(
    set => ({
      channelMode: 'single',
      setChannelMode: mode => set({ channelMode: mode }),
    }),
    {
      name: 'dspscript-channel-mode',
    },
  ),
)

interface DualModeCodeState {
  codeA: string
  codeB: string
  codeFileStateA?: CodeFileState
  codeFileStateB?: CodeFileState
  titleA: string
  artistA: string
  titleB: string
  artistB: string
  _hasHydrated: boolean
  setDualModeCode: (
    codeA: string,
    codeB: string,
    stateA?: CodeFileState,
    stateB?: CodeFileState,
  ) => void
  setDualModeCodeA: (code: string, state?: CodeFileState, title?: string, artist?: string) => void
  setDualModeCodeB: (code: string, state?: CodeFileState, title?: string, artist?: string) => void
  setHasHydrated: (state: boolean) => void
}

export const useDualModeCodeStore = create<DualModeCodeState>()(
  persist(
    (set, get) => ({
      codeA: '',
      codeB: '',
      codeFileStateA: undefined,
      codeFileStateB: undefined,
      titleA: '',
      artistA: '',
      titleB: '',
      artistB: '',
      _hasHydrated: false,
      setDualModeCode: (codeA, codeB, stateA, stateB) =>
        set({
          codeA,
          codeB,
          codeFileStateA: stateA,
          codeFileStateB: stateB,
        }),
      setDualModeCodeA: (code, state, title, artist) =>
        set({
          codeA: code,
          codeFileStateA: state,
          titleA: title !== undefined ? title : get().titleA,
          artistA: artist !== undefined ? artist : get().artistA,
        }),
      setDualModeCodeB: (code, state, title, artist) =>
        set({
          codeB: code,
          codeFileStateB: state,
          titleB: title !== undefined ? title : get().titleB,
          artistB: artist !== undefined ? artist : get().artistB,
        }),
      setHasHydrated: state => set({ _hasHydrated: state }),
    }),
    {
      name: 'dspscript-dual-mode-code',
      onRehydrateStorage: () => state => {
        state?.setHasHydrated(true)
      },
    },
  ),
)

interface CrossfadeState {
  crossfade: number
  mixVolumeA: number
  mixVolumeB: number
  setCrossfade: (value: number) => void
  setMixVolumeA: (value: number) => void
  setMixVolumeB: (value: number) => void
}

export const useCrossfadeStore = create<CrossfadeState>()(
  persist(
    set => ({
      crossfade: 0.5,
      mixVolumeA: 1.0,
      mixVolumeB: 1.0,
      setCrossfade: value => set({ crossfade: value }),
      setMixVolumeA: value => set({ mixVolumeA: value }),
      setMixVolumeB: value => set({ mixVolumeB: value }),
    }),
    {
      name: 'dspscript-crossfade',
    },
  ),
)

interface MasterVolumeState {
  volume: number
  setVolume: (value: number) => void
}

export const useMasterVolumeStore = create<MasterVolumeState>()(
  persist(
    set => ({
      volume: 1,
      setVolume: value => {
        dspscript.setMasterVolume(value)
        set({ volume: value })
      },
    }),
    {
      name: 'dspscript-master-volume',
      onRehydrateStorage: () => state => {
        if (state) {
          dspscript.setMasterVolume(state.volume)
        }
      },
    },
  ),
)

interface GlobalBpmState {
  globalBpm: number
  setGlobalBpm: (value: number) => void
}

export const useGlobalBpmStore = create<GlobalBpmState>()(
  persist(
    set => ({
      globalBpm: 120,
      setGlobalBpm: value => {
        dspscript.setGlobalBpm(value)
        set({ globalBpm: value })
      },
    }),
    {
      name: 'dspscript-global-bpm',
      onRehydrateStorage: () => state => {
        if (state) {
          dspscript.setGlobalBpm(state.globalBpm)
        }
      },
    },
  ),
)

interface OversamplingState {
  oversampling: 1 | 2 | 4 | 8
  setOversampling: (value: 1 | 2 | 4 | 8) => void
}

export const useOversamplingStore = create<OversamplingState>()(
  persist(
    set => ({
      oversampling: 1,
      setOversampling: value => {
        dspscript.setOversampling(value).catch(err => {
          console.error('Failed to set oversampling:', err)
        })
        set({ oversampling: value })
      },
    }),
    {
      name: 'dspscript-oversampling',
      onRehydrateStorage: () => state => {
        if (state) {
          dspscript.setOversampling(state.oversampling).catch(err => {
            console.error('Failed to set oversampling on rehydrate:', err)
          })
        }
      },
    },
  ),
)

interface ExportSettingsState {
  exportBarsSliderIndex: number
  exportOversampling: 1 | 2 | 4 | 8
  setExportBarsSliderIndex: (index: number) => void
  setExportOversampling: (value: 1 | 2 | 4 | 8) => void
}

export const useExportSettingsStore = create<ExportSettingsState>()(
  persist(
    set => ({
      exportBarsSliderIndex: 4, // Default to 16 bars (index 4)
      exportOversampling: 1,
      setExportBarsSliderIndex: index => set({ exportBarsSliderIndex: index }),
      setExportOversampling: value => set({ exportOversampling: value }),
    }),
    {
      name: 'dspscript-export-settings',
    },
  ),
)

interface ExportedAudioState {
  exportedAudioUrl: string | null
  exportedFilename: string
  setExportedAudio: (url: string | null, filename: string) => void
  clearExportedAudio: () => void
}

export const useExportedAudioStore = create<ExportedAudioState>(set => ({
  exportedAudioUrl: null,
  exportedFilename: 'export.wav',
  setExportedAudio: (url, filename) => {
    // Clean up previous URL if exists
    const currentState = useExportedAudioStore.getState()
    if (currentState.exportedAudioUrl) {
      URL.revokeObjectURL(currentState.exportedAudioUrl)
    }
    set({ exportedAudioUrl: url, exportedFilename: filename })
  },
  clearExportedAudio: () => {
    const currentState = useExportedAudioStore.getState()
    if (currentState.exportedAudioUrl) {
      URL.revokeObjectURL(currentState.exportedAudioUrl)
    }
    set({ exportedAudioUrl: null, exportedFilename: 'export.wav' })
  },
}))

interface LatencyHintState {
  latencyHint: number
  setLatencyHint: (value: number) => void
}

const latencyHintStorage: PersistStorage<LatencyHintState> = {
  getItem: (name: string) => {
    if (typeof window === 'undefined') return null

    // First check the new key
    const newValue = localStorage.getItem(name)
    if (newValue) {
      try {
        return JSON.parse(newValue)
      }
      catch {
        return null
      }
    }

    // If not found, check old key and migrate
    const oldValue = localStorage.getItem('latencyHint')
    if (oldValue) {
      const parsed = parseFloat(oldValue)
      if (!isNaN(parsed) && parsed >= 0) {
        // Migrate to new format
        const migrated = { state: { latencyHint: parsed }, version: 0 }
        try {
          localStorage.setItem(name, JSON.stringify(migrated))
          localStorage.removeItem('latencyHint')
        }
        catch {
          // Ignore storage errors
        }
        return migrated
      }
    }

    return null
  },
  setItem: (name: string, value: any): void => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(name, JSON.stringify(value))
      }
      catch {
        // Ignore storage errors
      }
    }
  },
  removeItem: (name: string): void => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(name)
      }
      catch {
        // Ignore storage errors
      }
    }
  },
}

export const useLatencyHintStore = create<LatencyHintState>()(
  persist(
    set => ({
      latencyHint: 0.0125,
      setLatencyHint: value => {
        set({ latencyHint: value })
      },
    }),
    {
      name: 'dspscript-latency-hint',
      storage: latencyHintStorage,
    },
  ),
)

interface VisualizerState {
  verticalIndex: number
  setVerticalIndex: (index: number) => void
  cycleVerticalIndex: () => void
}

export const useVisualizerStore = create<VisualizerState>(set => ({
  verticalIndex: 0,
  setVerticalIndex: (index: number) => set({ verticalIndex: index }),
  cycleVerticalIndex: () => set(state => ({ verticalIndex: (state.verticalIndex + 1) % 2 })),
}))

interface NavBarHeightState {
  height: number
  setHeight: (height: number) => void
}

export const useNavBarHeightStore = create<NavBarHeightState>(set => ({
  height: 60,
  setHeight: (height: number) => set({ height }),
}))

export type CompileMode = 'key' | 'ctrl+enter'

interface CompileModeState {
  compileMode: CompileMode
  setCompileMode: (mode: 'key' | 'ctrl+enter') => void
}

export const useCompileModeStore = create<CompileModeState>()(
  persist(
    set => ({
      compileMode: 'key',
      setCompileMode: mode => set({ compileMode: mode }),
    }),
    {
      name: 'dspscript-compile-mode',
    },
  ),
)

export interface PublicTracksState {
  tracks: any[]
  cursor: string | undefined
  hasMore: boolean
  setTracks: (tracks: any[] | ((prev: any[]) => any[])) => void
  appendTracks: (tracks: any[]) => void
  setCursor: (cursor: string | undefined) => void
  setHasMore: (hasMore: boolean) => void
  reset: () => void
}

let preservedState: { tracks: any[]; cursor: string | undefined; hasMore: boolean } | null = null

export const usePublicTracksStore = create<PublicTracksState>(set => {
  const initialState = preservedState
    ? {
      tracks: preservedState.tracks,
      cursor: preservedState.cursor,
      hasMore: preservedState.hasMore,
    }
    : { tracks: [], cursor: undefined, hasMore: true }

  return {
    ...initialState,
    setTracks: tracks => set(state => ({ tracks: typeof tracks === 'function' ? tracks(state.tracks) : tracks })),
    appendTracks: tracks => set(state => ({ tracks: [...state.tracks, ...tracks] })),
    setCursor: cursor => set({ cursor }),
    setHasMore: hasMore => set({ hasMore }),
    reset: () => set({ tracks: [], cursor: undefined, hasMore: true }),
  }
})

if (import.meta.hot) {
  import.meta.hot.on('vite:beforeUpdate', () => {
    const state = usePublicTracksStore.getState()
    preservedState = {
      tracks: state.tracks,
      cursor: state.cursor,
      hasMore: state.hasMore,
    }
  })
}

type AIModel = 'gpt-5-chat-latest' | 'chatgpt-4o-latest'

interface AISettingsState {
  temperature: number
  topP: number
  model: AIModel
  messageNew: string
  messageModify: string
  setTemperature: (value: number) => void
  setTopP: (value: number) => void
  setModel: (value: AIModel) => void
  setMessageNew: (value: string) => void
  setMessageModify: (value: string) => void
}

export const useAISettingsStore = create<AISettingsState>()(
  persist(
    set => ({
      temperature: 1.3,
      topP: 0.82,
      model: 'gpt-5-chat-latest',
      messageNew: '',
      messageModify: '',
      setTemperature: value => set({ temperature: value }),
      setTopP: value => set({ topP: value }),
      setModel: value => set({ model: value }),
      setMessageNew: value => set({ messageNew: value }),
      setMessageModify: value => set({ messageModify: value }),
    }),
    {
      name: 'dspscript-ai-settings',
    },
  ),
)

interface AutoDJSettingsState {
  secondsToSwap: number
  mixDuration: number
  message: string
  setSecondsToSwap: (value: number) => void
  setMixDuration: (value: number) => void
  setMessage: (value: string) => void
}

export const useAutoDJSettingsStore = create<AutoDJSettingsState>()(
  persist(
    set => ({
      secondsToSwap: 30,
      mixDuration: 20,
      message: '',
      setSecondsToSwap: value => set({ secondsToSwap: value }),
      setMixDuration: value => set({ mixDuration: value }),
      setMessage: value => set({ message: value }),
    }),
    {
      name: 'dspscript-autodj-settings',
    },
  ),
)

export interface GeneratedTrack {
  id: string
  title: string
  code: string
}

interface AIGeneratedTracksState {
  tracks: GeneratedTrack[]
  lastPlayedTrackId: string | null
  addTrack: (track: GeneratedTrack) => void
  setTracks: (tracks: GeneratedTrack[]) => void
  setLastPlayedTrackId: (id: string | null) => void
  clearTracks: () => void
}

export const useAIGeneratedTracksStore = create<AIGeneratedTracksState>(set => ({
  tracks: [],
  lastPlayedTrackId: null,
  addTrack: (track: GeneratedTrack) =>
    set(state => ({
      tracks: [track, ...state.tracks],
    })),
  setTracks: (tracks: GeneratedTrack[]) => set({ tracks }),
  setLastPlayedTrackId: (id: string | null) => set({ lastPlayedTrackId: id }),
  clearTracks: () => set({ tracks: [], lastPlayedTrackId: null }),
}))

interface AuthState {
  user: User | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, name: string) => Promise<void>
  signOut: () => Promise<void>
  setPassword: (password: string) => Promise<void>
  refreshUser: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  refreshUser: async () => {
    try {
      const response = await api.auth.getMe()
      set({ user: response.user })
    }
    catch {
      set({ user: null })
    }
  },
  signIn: async (email: string, password: string) => {
    const response = await api.auth.signIn(email, password)
    // Use a microtask to update state after current execution completes
    // This prevents remounts by ensuring the update happens after modal closes
    await Promise.resolve()
    set({ user: response.user })
  },
  signUp: async (email: string, password: string, name: string) => {
    const response = await api.auth.signUp(email, password, name)
    set({ user: response.user })
  },
  signOut: async () => {
    await api.auth.signOut()
    // Use a microtask to update state after current execution completes
    // This prevents remounts by ensuring the update happens smoothly
    await Promise.resolve()
    set({ user: null })
  },
  setPassword: async (password: string) => {
    await api.auth.setPassword(password)
    await get().refreshUser()
  },
}))

// Initialize auth on app start
if (typeof window !== 'undefined') {
  const initAuth = async () => {
    await useAuthStore.getState().refreshUser()
    useAuthStore.setState({ loading: false })

    // Check for session from Google OAuth redirect
    const params = new URLSearchParams(window.location.search)
    const needsPassword = params.get('needs_password')

    if (needsPassword === 'true') {
      await useAuthStore.getState().refreshUser()
    }
  }

  // Run initialization
  initAuth()
}

interface SessionState {
  editorInitiatedPlayback: boolean
  editorPlayingTabId: string | null
  publicTracksActiveTrackId: string | null
  publicTracksActiveRemixId: string | null
  trackPageActiveTrackId: string | null
  trackPageActiveRemixId: string | null
  profileActiveTrackId: string | null
  profileActiveRemixId: string | null
  remixTrack: any | null
  skipExampleOnce: boolean
  autoplayOnLoad: boolean
  setEditorInitiatedPlayback: (value: boolean) => void
  setEditorPlayingTabId: (id: string | null) => void
  setPublicTracksActiveTrackId: (id: string | null) => void
  setPublicTracksActiveRemixId: (id: string | null) => void
  setTrackPageActiveTrackId: (id: string | null) => void
  setTrackPageActiveRemixId: (id: string | null) => void
  setProfileActiveTrackId: (id: string | null) => void
  setProfileActiveRemixId: (id: string | null) => void
  setRemixTrack: (data: any | null) => void
  setSkipExampleOnce: (value: boolean) => void
  setAutoplayOnLoad: (value: boolean) => void
  removeEditorInitiatedPlayback: () => void
  removeEditorPlayingTabId: () => void
  removePublicTracksActiveTrackId: () => void
  removePublicTracksActiveRemixId: () => void
  removeTrackPageActiveTrackId: () => void
  removeTrackPageActiveRemixId: () => void
  removeProfileActiveTrackId: () => void
  removeProfileActiveRemixId: () => void
  removeRemixTrack: () => void
  removeSkipExampleOnce: () => void
  removeAutoplayOnLoad: () => void
  getEditorInitiatedPlayback: () => boolean
  getEditorPlayingTabId: () => string | null
  getPublicTracksActiveTrackId: () => string | null
  getPublicTracksActiveRemixId: () => string | null
  getTrackPageActiveTrackId: () => string | null
  getTrackPageActiveRemixId: () => string | null
  getProfileActiveTrackId: () => string | null
  getProfileActiveRemixId: () => string | null
  getRemixTrack: () => any | null
  getSkipExampleOnce: () => boolean
  getAutoplayOnLoad: () => boolean
}

export const useSessionStore = create<SessionState>((set, get) => ({
  editorInitiatedPlayback: false,
  editorPlayingTabId: null,
  publicTracksActiveTrackId: null,
  publicTracksActiveRemixId: null,
  trackPageActiveTrackId: null,
  trackPageActiveRemixId: null,
  profileActiveTrackId: null,
  profileActiveRemixId: null,
  remixTrack: null,
  skipExampleOnce: false,
  autoplayOnLoad: false,
  setEditorInitiatedPlayback: (value: boolean) => set({ editorInitiatedPlayback: value }),
  setEditorPlayingTabId: (id: string | null) => set({ editorPlayingTabId: id }),
  setPublicTracksActiveTrackId: (id: string | null) => set({ publicTracksActiveTrackId: id }),
  setPublicTracksActiveRemixId: (id: string | null) => set({ publicTracksActiveRemixId: id }),
  setTrackPageActiveTrackId: (id: string | null) => set({ trackPageActiveTrackId: id }),
  setTrackPageActiveRemixId: (id: string | null) => set({ trackPageActiveRemixId: id }),
  setProfileActiveTrackId: (id: string | null) => set({ profileActiveTrackId: id }),
  setProfileActiveRemixId: (id: string | null) => set({ profileActiveRemixId: id }),
  setRemixTrack: (data: any | null) => set({ remixTrack: data }),
  setSkipExampleOnce: (value: boolean) => set({ skipExampleOnce: value }),
  setAutoplayOnLoad: (value: boolean) => set({ autoplayOnLoad: value }),
  removeEditorInitiatedPlayback: () => set({ editorInitiatedPlayback: false }),
  removeEditorPlayingTabId: () => set({ editorPlayingTabId: null }),
  removePublicTracksActiveTrackId: () => set({ publicTracksActiveTrackId: null }),
  removePublicTracksActiveRemixId: () => set({ publicTracksActiveRemixId: null }),
  removeTrackPageActiveTrackId: () => set({ trackPageActiveTrackId: null }),
  removeTrackPageActiveRemixId: () => set({ trackPageActiveRemixId: null }),
  removeProfileActiveTrackId: () => set({ profileActiveTrackId: null }),
  removeProfileActiveRemixId: () => set({ profileActiveRemixId: null }),
  removeRemixTrack: () => set({ remixTrack: null }),
  removeSkipExampleOnce: () => set({ skipExampleOnce: false }),
  removeAutoplayOnLoad: () => set({ autoplayOnLoad: false }),
  getEditorInitiatedPlayback: () => get().editorInitiatedPlayback,
  getEditorPlayingTabId: () => get().editorPlayingTabId,
  getPublicTracksActiveTrackId: () => get().publicTracksActiveTrackId,
  getPublicTracksActiveRemixId: () => get().publicTracksActiveRemixId,
  getTrackPageActiveTrackId: () => get().trackPageActiveTrackId,
  getTrackPageActiveRemixId: () => get().trackPageActiveRemixId,
  getProfileActiveTrackId: () => get().profileActiveTrackId,
  getProfileActiveRemixId: () => get().profileActiveRemixId,
  getRemixTrack: () => get().remixTrack,
  getSkipExampleOnce: () => get().skipExampleOnce,
  getAutoplayOnLoad: () => get().autoplayOnLoad,
}))

interface EditorErrorsState {
  errorsA: EditorError[]
  errorsB: EditorError[]
  dismissedA: boolean
  dismissedB: boolean
  setErrorsA: (errors: EditorError[]) => void
  setErrorsB: (errors: EditorError[]) => void
  setDismissedA: (dismissed: boolean) => void
  setDismissedB: (dismissed: boolean) => void
}

export const useEditorErrorsStore = create<EditorErrorsState>(set => ({
  errorsA: [],
  errorsB: [],
  dismissedA: false,
  dismissedB: false,
  setErrorsA: errors => set({ errorsA: errors, dismissedA: false }),
  setErrorsB: errors => set({ errorsB: errors, dismissedB: false }),
  setDismissedA: dismissed => set({ dismissedA: dismissed }),
  setDismissedB: dismissed => set({ dismissedB: dismissed }),
}))

interface EditorStatusState {
  status: string
  setStatus: (status: string) => void
}

export const useEditorStatusStore = create<EditorStatusState>(set => ({
  status: 'Ready',
  setStatus: status => set({ status }),
}))

interface SidebarTabState {
  sidebarTab: 'compiled' | 'tracks' | 'liked'
  _hasHydrated: boolean
  setSidebarTab: (tab: 'compiled' | 'tracks' | 'liked') => void
  setHasHydrated: (state: boolean) => void
}

export const useSidebarTabStore = create<SidebarTabState>()(
  persist(
    (set, get) => ({
      sidebarTab: 'tracks',
      _hasHydrated: false,
      setSidebarTab: tab => set({ sidebarTab: tab }),
      setHasHydrated: state => set({ _hasHydrated: state }),
    }),
    {
      name: 'dspscript-sidebar-tab',
      onRehydrateStorage: () => state => {
        state?.setHasHydrated(true)
      },
    },
  ),
)

interface EditorSettingsState {
  hideFunctionSignatures: boolean
  wordWrap: boolean
  setHideFunctionSignatures: (value: boolean) => void
  setWordWrap: (value: boolean) => void
}

export const useEditorSettingsStore = create<EditorSettingsState>()(
  persist(
    set => ({
      hideFunctionSignatures: false,
      wordWrap: true,
      setHideFunctionSignatures: value => set({ hideFunctionSignatures: value }),
      setWordWrap: value => set({ wordWrap: value }),
    }),
    {
      name: 'dspscript-editor-settings',
    },
  ),
)

interface FontState {
  currentFont: string
  previewFont: string | null
  setFont: (font: string) => void
  setPreviewFont: (font: string | null) => void
}

export const useFontStore = create<FontState>()(
  persist(
    set => ({
      currentFont: 'IBM Plex Mono',
      previewFont: null,
      setFont: (font: string) => set({ currentFont: font }),
      setPreviewFont: (previewFont: string | null) => set({ previewFont }),
    }),
    {
      name: 'dspscript-font',
    },
  ),
)

interface UserProfileMenuState {
  closeMenu: () => void
  setCloseMenu: (fn: () => void) => void
  closeCallbacks: Array<() => void>
  addCloseCallback: (fn: () => void) => void
  removeCloseCallback: (fn: () => void) => void
}

export const useUserProfileMenuStore = create<UserProfileMenuState>((set, get) => ({
  closeMenu: () => {
    get().closeCallbacks.forEach(cb => cb())
  },
  setCloseMenu: (fn: () => void) =>
    set(state => ({
      closeCallbacks: [fn],
    })),
  closeCallbacks: [],
  addCloseCallback: (fn: () => void) =>
    set(state => ({
      closeCallbacks: [...state.closeCallbacks, fn],
    })),
  removeCloseCallback: (fn: () => void) =>
    set(state => ({
      closeCallbacks: state.closeCallbacks.filter(cb => cb !== fn),
    })),
}))

interface EditorControlsState {
  isLoading: boolean
  loadingCount: number
  isFullscreen: boolean
  setLoading: (loading: boolean) => void
  setIsFullscreen: (fullscreen: boolean) => void
  toggleFullscreen: () => void
}

export const useEditorControlsStore = create<EditorControlsState>((set, get) => ({
  isLoading: false,
  loadingCount: 0,
  isFullscreen: false,
  setLoading: (loading: boolean) => {
    if (loading) {
      const newCount = get().loadingCount + 1
      set({ loadingCount: newCount, isLoading: true })
    }
    else {
      const newCount = Math.max(0, get().loadingCount - 1)
      set({ loadingCount: newCount, isLoading: newCount > 0 })
    }
  },
  setIsFullscreen: (fullscreen: boolean) => set({ isFullscreen: fullscreen }),
  toggleFullscreen: () => set(state => ({ isFullscreen: !state.isFullscreen })),
}))

import type { BytecodeProgram } from './bytecode.ts'

interface CompiledOutputState {
  programA: BytecodeProgram | null
  programB: BytecodeProgram | null
  error: string | null
  setPrograms: (programA: BytecodeProgram | null, programB?: BytecodeProgram | null) => void
  setError: (error: string | null) => void
  reset: () => void
}

export const useCompiledOutputStore = create<CompiledOutputState>(set => ({
  programA: null,
  programB: null,
  error: 'Click "Compile" to see VM instructions...',
  setPrograms: (programA, programB = null) => set({ programA, programB, error: null }),
  setError: (error) => set({ error, programA: null, programB: null }),
  reset: () => set({ programA: null, programB: null, error: 'Click "Compile" to see VM instructions...' }),
}))
